import React, { useEffect, useRef, useState } from 'react';
import { Box, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, Tooltip, useTheme, Button, Checkbox, FormControlLabel, FormGroup, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { Edge } from './types';
import { useTranslation } from 'react-i18next';
import { getTenantCases, TENANT_CASE_BADGE_COLOR } from './lib/badgeUtils';

export interface EntityListEntry {
  id: string;
  name: string;
  type: 'Company' | 'Address' | 'CarRegNumber';
  connectionCount: number;
  next_layer_company_count?: number;
  coordinates?: [number, number] | null;
  data?: any;
  isHidden?: boolean;
  connectedCompanyNames?: string[];
  manufacturer?: string;
  model?: string;
  modelYear?: string;
  leasingCompany?: string;
}

interface EntityListPanelProps {
  companies: EntityListEntry[];
  addresses: EntityListEntry[];
  cars?: EntityListEntry[];
  showCarsTab?: boolean;
  enableTenantCases?: boolean;
  enableIvoLicenses?: boolean;
  relationships: Edge[];
  onSelectEntity: (entry: EntityListEntry) => void;
  onClose: () => void;
  mode?: 'map' | 'graph';
  sourceNodeId?: string;
}

type SortKey = 'name' | 'count';
type CarSortKey = 'company' | 'vehicle' | 'leaseGiver';

interface SortConfig {
  key: SortKey;
  direction: 'asc' | 'desc';
}

interface CarSortConfig {
  key: CarSortKey;
  direction: 'asc' | 'desc';
}

interface PanelPosition {
  x: number;
  y: number;
}

interface DragState {
  pointerId: number;
  clientX: number;
  clientY: number;
  position: PanelPosition;
  panelRect: DOMRect;
  boundaryRect: DOMRect;
}

const getConnectedCompanyNames = (car: EntityListEntry) => car.connectedCompanyNames?.join(', ') || '';

const getVehicleText = (car: EntityListEntry) => [
  car.name,
  car.manufacturer,
  car.model,
  car.modelYear,
].filter(Boolean).join(' ');

const compareText = (a: string, b: string, language: string) => {
  if (!a) return b ? 1 : 0;
  if (!b) return -1;
  return a.localeCompare(b, language, { numeric: true, sensitivity: 'base' });
};

const copyTextToClipboard = async (text: string): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back to execCommand for browsers that deny the Clipboard API.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  textArea.remove();
};

const EntityListPanel: React.FC<EntityListPanelProps> = ({ companies, addresses, cars = [], showCarsTab = false, enableTenantCases = false, enableIvoLicenses = false, relationships, onSelectEntity, onClose, mode = 'map', sourceNodeId }) => {
  const { t, i18n } = useTranslation();
  const tenantCasesLabel = t('graph.directTenantCases', {
    defaultValue: i18n.language?.startsWith('sv')
      ? 'Direkt kopplad till ett ärende'
      : 'Directly linked to a case',
  });
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const [panelPosition, setPanelPosition] = useState<PanelPosition>({ x: 0, y: 0 });
  const [panelSize, setPanelSize] = useState<{ width: number; height: number } | null>(null);
  const resizeStateRef = useRef<DragState | null>(null);
  const [tabValue, setTabValue] = useState(showCarsTab ? 2 : 0);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'name', direction: 'asc' });
  const [carSortConfig, setCarSortConfig] = useState<CarSortConfig>({ key: 'company', direction: 'asc' });
  const [filters, setFilters] = useState({
    hasDebt: false,
    isOnWatchlist: false,
    isBankrupt: false,
    hasTenantCase: false,
    hasIvo: false,
  });

  const handleFilterChange = (key: keyof typeof filters) => {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  };


  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCarSort = (key: CarSortKey) => {
    setCarSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedCars = () => [...cars].sort((a, b) => {
    const getSortValue = (car: EntityListEntry) => {
      if (carSortConfig.key === 'company') return getConnectedCompanyNames(car);
      if (carSortConfig.key === 'leaseGiver') return car.leasingCompany || '';
      return getVehicleText(car);
    };

    const comparison = compareText(getSortValue(a), getSortValue(b), i18n.language)
      || compareText(a.name, b.name, i18n.language);
    return carSortConfig.direction === 'asc' ? comparison : -comparison;
  });

  useEffect(() => {
    const resetPosition = () => { setPanelPosition({ x: 0, y: 0 }); setPanelSize(null); };
    window.addEventListener('resize', resetPosition);
    const boundary = panelRef.current?.parentElement;
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resetPosition) : null;
    if (boundary) observer?.observe(boundary);
    return () => { window.removeEventListener('resize', resetPosition); observer?.disconnect(); };
  }, []);

  const handleResizeStart = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !panelRef.current?.parentElement) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      pointerId: event.pointerId, clientX: event.clientX, clientY: event.clientY,
      position: panelPosition, panelRect: panelRef.current.getBoundingClientRect(),
      boundaryRect: panelRef.current.parentElement.getBoundingClientRect(),
    };
  };

  const resizePanel = (state: DragState, deltaX: number, deltaY: number) => {
    const availableWidth = Math.max(0, state.panelRect.right - state.boundaryRect.left - 8);
    const availableHeight = Math.max(0, state.boundaryRect.bottom - state.panelRect.top - 8);
    const width = Math.min(availableWidth, Math.max(Math.min(320, availableWidth), state.panelRect.width - deltaX));
    const height = Math.min(availableHeight, Math.max(Math.min(240, availableHeight), state.panelRect.height + deltaY));
    setPanelSize({ width, height });
    setPanelPosition(state.position);
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const movements: Record<string, PanelPosition> = { ArrowLeft: { x: -20, y: 0 }, ArrowRight: { x: 20, y: 0 }, ArrowUp: { x: 0, y: -20 }, ArrowDown: { x: 0, y: 20 } };
    const movement = movements[event.key];
    if (!movement || !panelRef.current?.parentElement) return;
    event.preventDefault();
    resizePanel({ pointerId: 0, clientX: 0, clientY: 0, position: panelPosition,
      panelRect: panelRef.current.getBoundingClientRect(), boundaryRect: panelRef.current.parentElement.getBoundingClientRect() }, movement.x, movement.y);
  };

  const handleDragStart = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || !panelRef.current?.parentElement) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    dragStateRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      position: panelPosition,
      panelRect: panelRef.current.getBoundingClientRect(),
      boundaryRect: panelRef.current.parentElement.getBoundingClientRect(),
    };
  };

  const handleDragMove = (event: React.PointerEvent<HTMLElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragState.clientX;
    const deltaY = event.clientY - dragState.clientY;
    const boundedDeltaX = Math.min(
      dragState.boundaryRect.right - dragState.panelRect.right,
      Math.max(dragState.boundaryRect.left - dragState.panelRect.left, deltaX),
    );
    const boundedDeltaY = Math.min(
      dragState.boundaryRect.bottom - dragState.panelRect.bottom,
      Math.max(dragState.boundaryRect.top - dragState.panelRect.top, deltaY),
    );

    setPanelPosition({
      x: dragState.position.x + boundedDeltaX,
      y: dragState.position.y + boundedDeltaY,
    });
  };

  const handleDragEnd = (event: React.PointerEvent<HTMLElement>) => {
    if (dragStateRef.current?.pointerId !== event.pointerId) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    dragStateRef.current = null;
  };

  const handleDragKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const movements: Record<string, PanelPosition> = {
      ArrowLeft: { x: -20, y: 0 },
      ArrowRight: { x: 20, y: 0 },
      ArrowUp: { x: 0, y: -20 },
      ArrowDown: { x: 0, y: 20 },
    };
    const movement = movements[event.key];
    const panel = panelRef.current;
    const boundary = panel?.parentElement;
    if (!movement || !panel || !boundary) return;

    event.preventDefault();
    const panelRect = panel.getBoundingClientRect();
    const boundaryRect = boundary.getBoundingClientRect();
    const boundedX = Math.min(
      boundaryRect.right - panelRect.right,
      Math.max(boundaryRect.left - panelRect.left, movement.x),
    );
    const boundedY = Math.min(
      boundaryRect.bottom - panelRect.bottom,
      Math.max(boundaryRect.top - panelRect.top, movement.y),
    );
    setPanelPosition(current => ({ x: current.x + boundedX, y: current.y + boundedY }));
  };

  const handleCopy = (event: React.MouseEvent, value: string) => {
    event.stopPropagation();
    void copyTextToClipboard(value).catch(() => undefined);
  };

  const renderCopyButton = (value: string, label: string) => (
    <Tooltip title={label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={(event) => handleCopy(event, value)}
        sx={{ color: 'inherit', opacity: 0.65, '&:hover': { opacity: 1 } }}
      >
        <ContentCopyIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );

  const handleExport = () => {
    const wb = XLSX.utils.book_new();

    // Prepare Companies data
    const companiesData = companies.map(c => ({
      [t('graph.exportColumns.companyName')]: c.name,
      [t('graph.exportColumns.organizationNumber')]: c.data?.properties?.organization_number || '',
      [t('graph.exportColumns.type')]: t('graph.company'),
      [t('graph.exportColumns.connections')]: c.connectionCount,
      [t('graph.exportColumns.nextLayerCompanies')]: c.next_layer_company_count,
      [t('graph.exportColumns.hidden')]: t(c.isHidden ? 'common.states.yes' : 'common.states.no'),
    }));
    const wsCompanies = XLSX.utils.json_to_sheet(companiesData);
    XLSX.utils.book_append_sheet(wb, wsCompanies, t('graph.exportSheets.companies'));

    // Prepare Addresses data
    const addressesData = addresses.map(a => ({
      [t('common.fields.name')]: a.name,
      [t('graph.exportColumns.type')]: t('graph.address'),
      [t('graph.exportColumns.connections')]: a.connectionCount,
      [t('graph.exportColumns.hidden')]: t(a.isHidden ? 'common.states.yes' : 'common.states.no'),
    }));
    const wsAddresses = XLSX.utils.json_to_sheet(addressesData);
    XLSX.utils.book_append_sheet(wb, wsAddresses, t('graph.exportSheets.addresses'));

    if (showCarsTab) {
      const carsData = getSortedCars().map(car => ({
        [t('graph.company')]: getConnectedCompanyNames(car),
        [t('graph.leasedVehicles')]: [car.name, car.manufacturer, car.model, car.modelYear].filter(Boolean).join(' '),
        [t('graph.leaseGiver')]: car.leasingCompany || '',
      }));
      const wsCars = XLSX.utils.json_to_sheet(carsData);
      XLSX.utils.book_append_sheet(wb, wsCars, t('graph.exportSheets.cars'));
    }

    // Prepare CompaniesAddresses data
    const companyMap = new Map(companies.map(c => [c.id, c]));
    const addressMap = new Map(addresses.map(a => [a.id, a]));

    const companiesAddressesData: any[] = [];
    
    relationships.forEach(rel => {
        const fromNode = companyMap.get(rel.from) || addressMap.get(rel.from);
        const toNode = companyMap.get(rel.to) || addressMap.get(rel.to);

        if (fromNode && toNode) {
            let company = null;
            let address = null;

            if (fromNode.type === 'Company' && toNode.type === 'Address') {
                company = fromNode;
                address = toNode;
            } else if (fromNode.type === 'Address' && toNode.type === 'Company') {
                address = fromNode;
                company = toNode;
            }

            if (company && address) {
                companiesAddressesData.push({
                    [t('graph.exportColumns.companyName')]: company.name,
                    [t('graph.exportColumns.organizationNumber')]: company.data?.properties?.organization_number || '',
                    [t('graph.exportColumns.address')]: address.name,
                    [t('graph.exportColumns.startDate')]: rel.properties?.start_date || '',
                    [t('graph.exportColumns.endDate')]: rel.properties?.end_date || '',
                });
            }
        }
    });
    
    const wsCompaniesAddresses = XLSX.utils.json_to_sheet(companiesAddressesData);
    XLSX.utils.book_append_sheet(wb, wsCompaniesAddresses, t('graph.exportSheets.companyAddresses'));

    const fileBase = t('graph.exportSheets.fileBase');
    const fileName = sourceNodeId ? `${fileBase}_${sourceNodeId}.xlsx` : `${fileBase}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const colors = {
    background: theme.palette.background.paper,
    headerBackground: isDarkMode ? '#1e293b' : '#f8fafc',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    textPrimary: isDarkMode ? '#f1f5f9' : '#0f172a',
    textSecondary: isDarkMode ? '#e2e8f0' : '#334155',
    hover: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
    shadow: isDarkMode ? '0px 4px 20px rgba(0, 0, 0, 0.5)' : '0px 4px 20px rgba(0, 0, 0, 0.1)',
    sortLabel: isDarkMode ? '#e2e8f0' : '#334155',
    sortLabelHover: isDarkMode ? '#fff' : '#000',
    icon: isDarkMode ? '#94a3b8' : '#64748b',
    noData: isDarkMode ? '#94a3b8' : '#64748b',
    tabText: isDarkMode ? '#94a3b8' : '#64748b',
  };

  // Styles matching HoverDataUtils.css and Palantir theme
  const tableStyles = {
    backgroundColor: colors.background,
    // border: `1px solid ${colors.border}`, // Handled by outer container
    // borderRadius: '12px', // Handled by outer container
    overflow: 'auto',
    // boxShadow: colors.shadow, // Handled by outer container
  };

  const headerStyles = {
    backgroundColor: colors.headerBackground,
    color: colors.textSecondary,
    fontWeight: 600,
    fontSize: 'var(--font-size-xs)',
    borderBottom: `1px solid ${colors.border}`,
    padding: '12px',
    fontFamily: 'var(--font-family)',
  };

  const cellStyles = {
    color: colors.textPrimary,
    fontSize: 'var(--font-size-xs)',
    borderBottom: `1px solid ${colors.border}`,
    padding: '10px',
    fontFamily: 'var(--font-family)',
    cursor: 'pointer',
  };

  const rowHoverStyles = {
    '&:hover': {
      backgroundColor: colors.hover,
    },
    '&:last-child td': {
      borderBottom: 'none',
    }
  };

  const renderTable = (data: EntityListEntry[], countHeader: string) => {
    // Apply filters
    const filteredData = data.filter(entry => {
      // Only apply these filters to Companies (tab 0) usually, but logic works for both if data available.
      if (entry.type !== 'Company' && (filters.hasDebt || filters.isOnWatchlist || filters.isBankrupt || (enableTenantCases && filters.hasTenantCase) || (enableIvoLicenses && filters.hasIvo))) {
          // If filtering specifically for company attributes, show/hide addresses?
          // Let's default to not hiding addresses unless they explicitly fail (which they don't have properties).
          // But strict filtering implies "Show only X". An address is not X.
          return false;
      }
      
      if (entry.type !== 'Company') return true;

      const props = entry.data?.properties;
      const onWatchlist = (entry.data as any)?.isOnWatchList;
      
      if (filters.hasDebt && !(props?.total_debt > 0 || props?.active_debt_amount > 0)) return false;
      if (filters.isOnWatchlist && !onWatchlist) return false;
      if (filters.isBankrupt && !((props?.konkurs_inledd && props?.konkurs_inledd !== "") && (!props?.konkurs_avslutad || props?.konkurs_avslutad === ""))) return false;
      if (enableTenantCases && filters.hasTenantCase && getTenantCases(props).length === 0) return false;
      if (enableIvoLicenses && filters.hasIvo && props?.has_ivo_license !== true) return false;
      
      return true;
    });

    const sortedData = [...filteredData].sort((a, b) => {
      let comparison = 0;
      if (sortConfig.key === 'name') {
        comparison = a.name.localeCompare(b.name, i18n.language);
      } else {
        const getCount = (entry: EntityListEntry) => 
          entry.type === 'Company' && entry.next_layer_company_count !== undefined 
            ? entry.next_layer_company_count 
            : entry.connectionCount;
        comparison = getCount(a) - getCount(b);
      }
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return (
    <TableContainer sx={{ overflow: 'auto', maxHeight: '100%', backgroundColor: colors.background }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerStyles}>
              <TableSortLabel
                active={sortConfig.key === 'name'}
                direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('name')}
                sx={{
                  '&.MuiTableSortLabel-root': { color: colors.sortLabel },
                  '&.MuiTableSortLabel-root:hover': { color: colors.sortLabelHover },
                  '&.Mui-active': { color: '#3b82f6' },
                  '& .MuiTableSortLabel-icon': { color: '#3b82f6 !important' },
                }}
              >
                {t('common.fields.name')}
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ ...headerStyles, width: '80px' }}>
              <TableSortLabel
                active={sortConfig.key === 'count'}
                direction={sortConfig.key === 'count' ? sortConfig.direction : 'asc'}
                onClick={() => handleSort('count')}
                sx={{
                  '&.MuiTableSortLabel-root': { color: colors.sortLabel },
                  '&.MuiTableSortLabel-root:hover': { color: colors.sortLabelHover },
                  '&.Mui-active': { color: '#3b82f6' },
                  '& .MuiTableSortLabel-icon': { color: '#3b82f6 !important' },
                }}
              >
                {countHeader}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row) => {
            const canLocate = mode === 'graph' || (mode === 'map' && !!row.coordinates);
            
            // Prepare badges
            const props = row.data?.properties;
            const onWatchlist = (row.data as any)?.isOnWatchList;
            // Check for active bankruptcy: has start date but no end date
            const isBankrupt = (props?.konkurs_inledd && props?.konkurs_inledd !== "") && 
                               (!props?.konkurs_avslutad || props?.konkurs_avslutad === "");
            const hasDebt = props?.total_debt > 0 || props?.active_debt_amount > 0;
            const nextLayerCompanyCount = props?.next_layer_company_count || 0;
            const nextLayerDebtCount = props?.next_layer_active_debt_count || 0;
            const displayCount = row.type === 'Company' && row.next_layer_company_count !== undefined
              ? row.next_layer_company_count
              : row.connectionCount;
            const tenantCases = enableTenantCases && row.type === 'Company' ? getTenantCases(props) : [];

            return (
              <TableRow 
                key={row.id} 
                sx={rowHoverStyles}
                onClick={() => canLocate && onSelectEntity(row)}
              >
                <TableCell sx={cellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      {row.name}
                      {row.isHidden && (
                        <Tooltip title={t('graph.hiddenFromGraph')}>
                          <VisibilityOffIcon sx={{ fontSize: 16, color: colors.icon }} />
                        </Tooltip>
                      )}
                      {onWatchlist && (
                        <Tooltip title={t('graph.onWatchlist')}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', backgroundColor: '#f39c12', color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>!</Box>
                        </Tooltip>
                      )}
                      {tenantCases.length > 0 && (
                        <Tooltip
                          title={
                            <Box>
                              <Box sx={{ fontWeight: 700, mb: 0.5 }}>{tenantCasesLabel}</Box>
                              {tenantCases.map((tenantCase) => (
                                <Box key={`${tenantCase.case_number}-${tenantCase.case_date}`}>
                                  {tenantCase.case_number} · {tenantCase.case_date} · {t(
                                    `graph.caseStatuses.${tenantCase.case_status.toLowerCase()}`,
                                    { defaultValue: tenantCase.case_status },
                                  )}
                                </Box>
                              ))}
                            </Box>
                          }
                        >
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            minWidth: 28, height: 18, px: 0.5, gap: 0.25, borderRadius: '9px',
                            backgroundColor: TENANT_CASE_BADGE_COLOR, color: '#fff', fontSize: '10px', fontWeight: 700,
                          }}>
                            <svg width="10" height="12" viewBox="0 0 12 14" aria-hidden="true">
                              <path d="M2 1.5h5l3 3v8H2z M7 1.5v3h3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                            </svg>
                            {tenantCases.length}
                          </Box>
                        </Tooltip>
                      )}
                      {isBankrupt && (
                        <Tooltip title={t('graph.inBankruptcy')}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', backgroundColor: '#000', color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>K</Box>
                        </Tooltip>
                      )}
                      {hasDebt && (
                        <Tooltip title={t('graph.hasDebt')}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', backgroundColor: '#dc3545', color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>S</Box>
                        </Tooltip>
                      )}
                      {nextLayerDebtCount > 0 && (
                        <Tooltip title={t('graph.nextLayerDebtCompanies')}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', backgroundColor: '#dc3545', color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>{nextLayerDebtCount}</Box>
                        </Tooltip>
                      )}
                      {nextLayerCompanyCount > 0 && (
                        <Tooltip title={t('graph.nextLayerCompanies')}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', backgroundColor: '#6c757d', color: '#fff', fontSize: '10px', fontWeight: 'bold' }}>{nextLayerCompanyCount}</Box>
                        </Tooltip>
                      )}
                    </Box>
                    {renderCopyButton(row.name, t('graph.copyCell'))}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={cellStyles}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                    {displayCount}
                    {renderCopyButton(String(displayCount), t('graph.copyCell'))}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {sortedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={2} sx={{ ...cellStyles, textAlign: 'center', color: colors.noData }}>
                {t('graph.noData')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
  };

  const renderCarsTable = () => {
    const sortedCars = getSortedCars();
    const carSortLabelStyles = {
      '&.MuiTableSortLabel-root': { color: colors.sortLabel },
      '&.MuiTableSortLabel-root:hover': { color: colors.sortLabelHover },
      '&.Mui-active': { color: '#3b82f6' },
      '& .MuiTableSortLabel-icon': { color: '#3b82f6 !important' },
    };

    return (
    <TableContainer sx={{ overflow: 'auto', maxHeight: '100%', backgroundColor: colors.background }}>
      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerStyles, width: '29%' }}>
              <TableSortLabel
                active={carSortConfig.key === 'company'}
                direction={carSortConfig.key === 'company' ? carSortConfig.direction : 'asc'}
                onClick={() => handleCarSort('company')}
                sx={carSortLabelStyles}
              >
                {t('graph.company')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...headerStyles, width: '32%' }}>
              <TableSortLabel
                active={carSortConfig.key === 'vehicle'}
                direction={carSortConfig.key === 'vehicle' ? carSortConfig.direction : 'asc'}
                onClick={() => handleCarSort('vehicle')}
                sx={carSortLabelStyles}
              >
                {t('graph.leasedVehicles')}
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ ...headerStyles, width: '31%' }}>
              <TableSortLabel
                active={carSortConfig.key === 'leaseGiver'}
                direction={carSortConfig.key === 'leaseGiver' ? carSortConfig.direction : 'asc'}
                onClick={() => handleCarSort('leaseGiver')}
                sx={carSortLabelStyles}
              >
                {t('graph.leaseGiver')}
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedCars.map(car => {
            const company = getConnectedCompanyNames(car) || '—';
            const vehicleDetails = [car.manufacturer, car.model].filter(Boolean).join(' ') || '—';
            const vehicle = [car.name, vehicleDetails, car.modelYear].filter(value => value && value !== '—').join(' ');
            const leaseGiver = car.leasingCompany || '—';

            return (
              <TableRow key={car.id} sx={rowHoverStyles} onClick={() => onSelectEntity(car)}>
                <TableCell sx={{ ...cellStyles, verticalAlign: 'top', overflowWrap: 'anywhere' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box>{company}</Box>
                    {renderCopyButton(company, t('graph.copyCell'))}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...cellStyles, verticalAlign: 'top', overflowWrap: 'anywhere' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box>
                      <Box sx={{ fontWeight: 700 }}>{car.name}</Box>
                      <Box sx={{ color: colors.textSecondary, mt: 0.25 }}>{vehicleDetails}</Box>
                      {car.modelYear && <Box sx={{ color: colors.textSecondary }}>{car.modelYear}</Box>}
                      {car.isHidden && (
                        <Tooltip title={t('graph.hiddenFromGraph')}>
                          <VisibilityOffIcon sx={{ fontSize: 16, color: colors.icon, mt: 0.5 }} />
                        </Tooltip>
                      )}
                    </Box>
                    {renderCopyButton(vehicle, t('graph.copyCell'))}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...cellStyles, verticalAlign: 'top', overflowWrap: 'anywhere' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                    <Box>{leaseGiver}</Box>
                    {renderCopyButton(leaseGiver, t('graph.copyCell'))}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
          {sortedCars.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ ...cellStyles, textAlign: 'center', color: colors.noData }}>
                {t('graph.noData')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    );
  };

  return (
    <div
      ref={panelRef}
      style={{
      position: 'absolute',
      top: 8,
      right: 8,
      width: panelSize?.width ?? (showCarsTab ? 'min(680px, calc(100% - 16px))' : 'min(500px, calc(100% - 16px))'),
      height: panelSize?.height ?? 'calc(100% - 16px)',
      display: 'flex', 
      flexDirection: 'column',
      background: 'transparent',
      zIndex: 1000,
      gap: '8px',
      pointerEvents: 'none',
      boxSizing: 'border-box',
      transform: `translate(${panelPosition.x}px, ${panelPosition.y}px)`,
    }}>
      <Paper sx={{
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        backgroundColor: colors.background,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        boxShadow: colors.shadow,
        pointerEvents: 'auto',
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            role="button"
            aria-label={t('graph.moveEntityList')}
            onPointerDown={handleDragStart}
            onPointerMove={handleDragMove}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
            onKeyDown={handleDragKeyDown}
            tabIndex={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexGrow: 1,
              minWidth: 0,
              color: colors.textSecondary,
              cursor: 'grab',
              userSelect: 'none',
              touchAction: 'none',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIndicatorIcon fontSize="small" />
            <Typography sx={{ fontSize: 'var(--font-size-small)', fontWeight: 700 }}>
              {t('graph.entityList')}
            </Typography>
          </Box>
          <Tooltip title={t('graph.fillEntityListHeight')}>
            <IconButton size="small" aria-label={t('graph.fillEntityListHeight')}
              onClick={() => { setPanelSize(null); setPanelPosition({ x: 0, y: 0 }); }}>
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.actions.close')}>
            <IconButton
              size="small"
              aria-label={t('common.actions.close')}
              onClick={onClose}
              sx={{ color: colors.icon }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: '36px',
            '& .MuiTab-root': {
              color: colors.tabText,
              fontFamily: 'var(--font-family)',
              fontSize: 'var(--font-size-small)',
              fontWeight: 600,
              minHeight: '36px',
              padding: '6px 12px',
              '&.Mui-selected': {
                color: '#3b82f6',
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b82f6',
            }
          }}
        >
          <Tab label={t('graph.companiesWithCount', { count: companies.length })} />
          <Tab label={t('graph.addressesWithCount', { count: addresses.length })} />
          {showCarsTab && <Tab label={t('graph.carsWithCount', { count: cars.length })} />}
        </Tabs>

        <Button 
          variant="outlined" 
          startIcon={<DownloadIcon />} 
          onClick={handleExport}
          size="small"
          sx={{ 
            color: colors.textPrimary, 
            borderColor: colors.border,
            textTransform: 'none',
            height: '32px',
            '&:hover': {
              borderColor: colors.textSecondary,
              backgroundColor: colors.hover
            }
          }}
        >
          {t('common.actions.export')}
        </Button>
        </Box>

        {tabValue === 0 && (
          <FormGroup row sx={{ 
            '& .MuiFormControlLabel-label': { fontSize: '11px', color: colors.textSecondary }, 
            gap: 1,
            px: 0.5 
          }}>
            <FormControlLabel 
              control={
                <Checkbox 
                  size="small" 
                  checked={filters.hasDebt} 
                  onChange={() => handleFilterChange('hasDebt')} 
                  sx={{ p: 0.5, color: '#dc3545', '&.Mui-checked': { color: '#dc3545' } }} 
                />
              } 
              label={t('graph.hasDebt')}
            />
            <FormControlLabel 
              control={
                <Checkbox 
                  size="small" 
                  checked={filters.isOnWatchlist} 
                  onChange={() => handleFilterChange('isOnWatchlist')} 
                  sx={{ p: 0.5, color: '#f39c12', '&.Mui-checked': { color: '#f39c12' } }} 
                />
              } 
              label={t('navigation.watchlist')}
            />
            <FormControlLabel 
              control={
                <Checkbox 
                  size="small" 
                  checked={filters.isBankrupt} 
                  onChange={() => handleFilterChange('isBankrupt')} 
                  sx={{ p: 0.5 }} 
                />
              } 
              label={t('graph.inBankruptcy')}
            />
            {enableIvoLicenses && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filters.hasIvo}
                    onChange={() => handleFilterChange('hasIvo')}
                    sx={{ p: 0.5 }}
                  />
                }
                label={t('graph.hasIvo')}
              />
            )}
            {enableTenantCases && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filters.hasTenantCase}
                    onChange={() => handleFilterChange('hasTenantCase')}
                    sx={{ p: 0.5, color: TENANT_CASE_BADGE_COLOR, '&.Mui-checked': { color: TENANT_CASE_BADGE_COLOR } }}
                  />
                }
                label={t('graph.hasCase')}
              />
            )}
          </FormGroup>
        )}
      </Paper>
      
      <Paper sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: colors.background,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        boxShadow: colors.shadow,
        overflow: 'hidden',
        pointerEvents: 'auto',
      }}>
        <Box sx={{ overflow: 'hidden', height: '100%' }}>
          {tabValue === 0 && renderTable(companies, t('graph.connections'))}
          {tabValue === 1 && renderTable(addresses, t('graph.directConnections'))}
          {showCarsTab && tabValue === 2 && renderCarsTable()}
        </Box>
      </Paper>
      <Box role="button" tabIndex={0} aria-label={t('graph.resizeEntityList')} title={t('graph.resizeEntityList')}
        onPointerDown={handleResizeStart}
        onPointerMove={event => {
          const state = resizeStateRef.current;
          if (state?.pointerId === event.pointerId) resizePanel(state, event.clientX - state.clientX, event.clientY - state.clientY);
        }}
        onPointerUp={event => {
          resizeStateRef.current = null;
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={() => { resizeStateRef.current = null; }}
        onKeyDown={handleResizeKeyDown}
        sx={{ position: 'absolute', left: 0, bottom: 0, width: 24, height: 24, cursor: 'nesw-resize', touchAction: 'none', pointerEvents: 'auto',
          color: colors.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: colors.background, borderRadius: '8px 0 8px 0',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}>
        <span aria-hidden="true" style={{ fontSize: 20 }}>◣</span>
      </Box>
    </div>
  );
};

export default EntityListPanel;
